import axios from "./axiosConfig";
import { getUploadUrl, uploadToS3 } from "./filesService";

async function createFieldReport(reportData) {
  try {
    const { eventId, serviceId, resultLevel, entryTime, exitTime, reportPdf } = reportData;

    if (!eventId) throw new Error("eventId es requerido");
    if (!serviceId) throw new Error("serviceId es requerido");
    if (resultLevel === undefined || resultLevel === null)
      throw new Error("resultLevel es requerido");
    if (!entryTime) throw new Error("entryTime es requerido");
    if (!exitTime) throw new Error("exitTime es requerido");
    if (!reportPdf) throw new Error("reportPdf es requerido");

    const level = parseInt(resultLevel, 10);
    if (isNaN(level) || level < 0 || level > 3) {
      throw new Error("resultLevel debe estar entre 0 y 3");
    }

    const entry = new Date(entryTime);
    const exit = new Date(exitTime);

    if (isNaN(entry.getTime())) throw new Error("entryTime es inválido");
    if (isNaN(exit.getTime())) throw new Error("exitTime es inválido");
    if (exit <= entry) throw new Error("exitTime debe ser posterior a entryTime");

    const serviceResponse = await axios.get(`/services/${serviceId}`);
    const serviceData = serviceResponse.data.data;

    const serviceDate = new Date(serviceData.date || serviceData.serviceDate || serviceData.createdAt);
    const year = serviceDate.getFullYear();
    const month = String(serviceDate.getMonth() + 1).padStart(2, "0");
    const day = String(serviceDate.getDate()).padStart(2, "0");
    const dateFolder = `soportes/${year}/${month}/${day}/`;

    const { uploadUrl, key } = await getUploadUrl(reportPdf, dateFolder);
    await uploadToS3(uploadUrl, reportPdf);

    const reportPdfUrl = `https://${process.env.REACT_APP_S3_BUCKET}.s3.amazonaws.com/${key}`;

    const response = await axios.post("/reports/field", {
      eventId,
      resultLevel: level,
      entryTime: entry.toISOString(),
      exitTime: exit.toISOString(),
      reportPdfUrl,
      reportPdfKey: key,
    });

    return {
      success: true,
      report: response.data.data,
      message: response.data.message,
    };
  } catch (err) {
    console.error("Error al crear reporte de campo:", err);
    throw new Error(
      err.response?.data?.message || err.message || "Error al crear el reporte de campo"
    );
  }
}

async function getAllReports(filters = {}) {
  try {
    const response = await axios.get("/reports/field", { params: filters });
    return response.data.data || [];
  } catch (err) {
    console.error("Error al obtener reportes:", err);
    throw err;
  }
}

async function getReportById(reportId) {
  try {
    const response = await axios.get(`/reports/field/${reportId}`);
    return response.data.data;
  } catch (err) {
    console.error("Error al obtener reporte:", err);
    throw err;
  }
}

async function getReportsByEventId(eventId) {
  try {
    const response = await axios.get(`/reports/field/event/${eventId}`);
    return response.data.data || [];
  } catch (err) {
    console.error("Error al obtener reportes del servicio:", err);
    throw err;
  }
}

async function updateReport(reportId, updateData) {
  try {
    const response = await axios.put(`/reports/field/${reportId}`, updateData);
    return response.data.data;
  } catch (err) {
    console.error("Error al actualizar reporte:", err);
    throw err;
  }
}

async function deleteReport(reportId) {
  try {
    await axios.delete(`/reports/field/${reportId}`);
    return { success: true, message: "Reporte eliminado exitosamente" };
  } catch (err) {
    console.error("Error al eliminar reporte:", err);
    throw err;
  }
}

async function getStatistics(filters = {}) {
  try {
    const response = await axios.get("/reports/field/statistics", { params: filters });
    return response.data.data;
  } catch (err) {
    console.error("Error al obtener estadísticas:", err);
    throw err;
  }
}

function pdfToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result.split(",")[1] || reader.result;
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function downloadReportPdf(pdfUrl, fileName = "reporte-campo.pdf") {
  try {
    const response = await axios.get(pdfUrl, { responseType: "blob" });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (err) {
    console.error("Error al descargar PDF:", err);
    throw err;
  }
}

const fieldReportService = {
  createFieldReport,
  getAllReports,
  getReportById,
  getReportsByEventId,
  updateReport,
  deleteReport,
  getStatistics,
  pdfToBase64,
  downloadReportPdf,
};

export default fieldReportService;
